import { BaseAPI } from "@utils/base-api";
import {
  DiscountApplicableChargeType,
  DiscountApplicableProduct,
  DiscountCodeType,
} from "@/primary-models";
import { AxiosResponse, isAxiosError } from "axios";
import { Product } from "libs/api/customer/src/lib/types/products";
import {
  MAX_SAVING,
  PRICING_MATRIX,
  type TLitterPricing,
} from "./litterApi.constants";

export type VariablePricingKey = number | "default";

export interface IVariablePricing {
  discount: number;
  unitPrice: number;
  quantity: number;
}

export type IQuantityToPrice = {
  quantityToPrice: Record<VariablePricingKey, IVariablePricing>;
};

export interface IUpcomingDiscount {
  name: string;
  expiresAt: string;
}

export interface ILitterDiscountValidator {
  validateDiscountCode(
    discountCode: string,
    options: {
      chargeType: DiscountApplicableChargeType;
      product: DiscountApplicableProduct;
    },
  ): Promise<
    AxiosResponse<{
      success: boolean;
      discount: {
        amount: unknown;
        code: string;
        type: DiscountCodeType;
      };
    }>
  >;
}

const DISCOUNT_UNIT_TYPES = {
  [DiscountCodeType.ABSOLUTE_TOTAL]: "absoluteTotal",
  [DiscountCodeType.FIXED_AMOUNT]: "fixedAmount",
  [DiscountCodeType.PERCENTAGE]: "percentage",
} as const satisfies Record<DiscountCodeType, string>;

const roundToTwoDecimalPlaces = (value: number): number =>
  Math.round(value * 100) / 100;

export class LitterAPI extends BaseAPI {

  private readonly discountsAPI: ILitterDiscountValidator | undefined;

  constructor(
    baseURL: string,
    accessToken?: string,
    discountsAPI?: ILitterDiscountValidator,
  ) {
    super(baseURL, accessToken);
    this.discountsAPI = discountsAPI;
  }

  public async getPricesByProductId(
    productTitle: Product,
  ): Promise<AxiosResponse<IQuantityToPrice>> {
    try {
      return await this.axios.get(
        `/customer/extraPlan/getProductPrices?productTitle=${productTitle}`,
      );
    } catch {
      throw new Error();
    }
  }

  public getPrices(): TLitterPricing[] {
    return Object.values(PRICING_MATRIX);
  }

  public async getPrice(
    quantity: number,
    options:
      | {
        discountCode: string;
        purchaseType: DiscountApplicableChargeType;
      }
      | undefined,
  ): Promise<TLitterPricing> {
    if (!Number.isInteger(quantity) || quantity < 1) {
      throw new Error("Quantity must be a positive integer.");
    }
    const defaultPricing = quantity > 4 ? MAX_SAVING : PRICING_MATRIX[quantity];

    if (!options) {
      return defaultPricing;
    }

    if (!this.discountsAPI) {
      throw new Error(
        "A discount validator is required to calculate discounted litter pricing.",
      );
    }

    let discountResponse: Awaited<
      ReturnType<ILitterDiscountValidator["validateDiscountCode"]>
    >;

    try {
      discountResponse = await this.discountsAPI.validateDiscountCode(
        options.discountCode,
        {
          chargeType: options.purchaseType,
          product: DiscountApplicableProduct.PLANET_SCOOP,
        },
      );
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return defaultPricing;
      }

      throw error;
    }

    const { discount } = discountResponse.data;

    const discountAmount = Number(discount.amount);

    if (
      !Number.isFinite(discountAmount) ||
      discountAmount < 0
    ) {
      throw new Error("Discount amount must be a non-negative number.");
    }

    let additionalSavingPercentage: number;
    let unitPrice: number;

    switch (discount.type) {
      case DiscountCodeType.PERCENTAGE: {
        if (discountAmount > 100) {
          throw new Error("Percentage discount must not exceed 100.");
        }

        additionalSavingPercentage = discountAmount;
        unitPrice = roundToTwoDecimalPlaces(
          defaultPricing.unitPrice * (1 - discountAmount / 100),
        );
        break;
      }
      case DiscountCodeType.FIXED_AMOUNT: {
        const totalPrice = roundToTwoDecimalPlaces(
          defaultPricing.unitPrice * quantity,
        );
        const discountedTotalPrice = roundToTwoDecimalPlaces(
          Math.max(0, totalPrice - discountAmount),
        );
        unitPrice = roundToTwoDecimalPlaces(discountedTotalPrice / quantity);
        additionalSavingPercentage =
          ((totalPrice - discountedTotalPrice) / totalPrice) * 100;
        break;
      }
      case DiscountCodeType.ABSOLUTE_TOTAL: {
        unitPrice = roundToTwoDecimalPlaces(
          Math.min(defaultPricing.unitPrice, discountAmount),
        );
        additionalSavingPercentage =
          ((defaultPricing.unitPrice - unitPrice) / defaultPricing.unitPrice) *
          100;
        break;
      }
      default: {
        const unexpectedDiscountType: never = discount.type;
        throw new Error(
          `Unsupported litter discount type: ${unexpectedDiscountType}`,
        );
      }
    }

    const discountSaving = roundToTwoDecimalPlaces(
      100 -
        (100 - defaultPricing.discountSaving) *
          (1 - additionalSavingPercentage / 100),
    );

    return {
      ...defaultPricing,
      discount: {
        code: discount.code,
        unit: discountAmount,
        unitType: DISCOUNT_UNIT_TYPES[discount.type],
      },
      discountSaving,
      unitPrice,
    };
  }

}
